import { Modal, Container, Row, Col, Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { AmazonSFPConfig } from './AmazonSFPConfigComponent'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { notify } from '../notify/notify'

interface AmazonSFPConfigEditProps {
  config?: { name: string, content: AmazonSFPConfig }
  visible: boolean
  closeModal: () => void
  saveConfig: (cfg: AmazonSFPConfig, name: string) => void
  deleteConfig: (configName: string) => void
  duplicateConfig: (e) => void
  existingConfigNames: string[]
  mode: 'edit' | 'duplicate' | 'new'
}

const AmazonSFPConfigEdit = ({ config, visible, closeModal, saveConfig, deleteConfig, duplicateConfig, existingConfigNames, mode }: AmazonSFPConfigEditProps) => {
  if (!config) return null

  const [baseName, setBaseName] = useState<string>('')
  const [fileName, setFileName] = useState<string>('')
  const [days, setDays] = useState<string[]>([])
  const [enabled, setEnabled] = useState<boolean>(true)
  const [time, setTime] = useState<string>('')
  const [emailTo, setEmailTo] = useState<string[]>([])
  const [listingEnabled, setListingEnabled] = useState<boolean>(false)

  useEffect(() => {
    const { name, content } = config
    const defaultBase = name ? name.split('/').pop()! : 'amazon.toggle.sfp.DESCRIBE.ENABLED-DISABLED.json'
    setBaseName(defaultBase)
    setFileName(`inventory-service/amazon/${defaultBase}`)
    setDays(content?.days || [])
    setEnabled(content?.enabled ?? true)
    setTime(content?.setTimeCentral || '')
    setEmailTo(content?.emailTo || [])
    setListingEnabled(content?.setListingTo ?? false)
  }, [config])

  const handleSave = useCallback(() => {
  if (!time || days.length === 0) {
    notify('error', 'Run At time and Days of Week are required.')
    return
  }

  const newBaseName = fileName.split('/').pop()!
  const oldBaseName = config.name?.split('/').pop()!

  const isRenaming = mode === 'edit' && newBaseName !== oldBaseName

  if (existingConfigNames.includes(newBaseName) && !isRenaming) {
    notify('error', 'Filename already exists. Please choose another.')
    return
  }

  const newCfg: AmazonSFPConfig = {
    days,
    emailTo,
    enabled,
    lastSet: config.content?.lastSet || DateTime.now().toISO(),
    listings: config.content?.listings || [],
    setListingTo: listingEnabled,
    setTimeCentral: time,
  }

  if (confirm(`Save configuration ${newBaseName}?`)) {
    saveConfig(newCfg, fileName)
    if (isRenaming) {
      deleteConfig(config.name)
    }
  }
}, [
  time, days, emailTo, enabled, listingEnabled, fileName,
  config, existingConfigNames, saveConfig, deleteConfig, mode
  ])

  const handleDelete = () => {
    if (confirm(`Are you sure you want to delete configuration ${config.name}?`)) {
      deleteConfig(config.name)
      closeModal()
    }
  }

  const onDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target
    setDays(prev => checked ? [...prev, value] : prev.filter(d => d !== value))
  }
  const onEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) => setEnabled(e.target.checked)
  const onTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => setTime(e.target.value)
  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => setEmailTo(e.target.value.split(', ').map(s => s.trim()))
  const onListingChange = (e: React.ChangeEvent<HTMLInputElement>) => setListingEnabled(e.target.checked)
  const onFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setFileName(`inventory-service/amazon/${e.target.value}`)

  const lastRun = config.content?.lastSet
    ? DateTime.fromISO(config.content.lastSet).setZone('America/Chicago').toLocaleString(DateTime.DATETIME_SHORT)
    : 'Never'

  const sortedDays = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']

  return (
    <Modal show={visible} onHide={closeModal} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{config.name ? config.name.split('/').pop() : 'New Configuration'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group as={Row} className="mb-2">
            <Form.Label column sm={3} className="text-right">Last Run:</Form.Label>
            <Col sm={9}>
              <Form.Control plaintext readOnly defaultValue={lastRun} className="fw-bold text-primary" />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-2">
            <Form.Label column sm={3} className="text-right">Schedule Status:</Form.Label>
            <Col sm={9}>
              <Form.Check type="switch" label={enabled ? 'Enabled' : 'Disabled'} checked={enabled} onChange={onEnabledChange} />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-2">
            <Form.Label column sm={3} className="text-right">Days of Week:</Form.Label>
            <Col sm={9} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'nowrap', overflowX: 'auto' }}>
              {sortedDays.map(day => (
                <Form.Check
                  key={day}
                  inline
                  type="checkbox"
                  label={day}
                  value={day}
                  checked={days.includes(day)}
                  onChange={onDaysChange}
                />
              ))}
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-2">
            <Form.Label column sm={3} className="text-right">Run At (CT):</Form.Label>
            <Col sm={9}>
              <Form.Control type="time" value={time} onChange={onTimeChange} />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-2">
            <Form.Label column sm={3} className="text-right">Email To:</Form.Label>
            <Col sm={9}>
              <Form.Control
                type="text"
                value={emailTo.join(', ')}
                onChange={onEmailChange}
                placeholder="a@b.com, c@d.com"
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-2">
            <Form.Label column sm={3} className="text-right">Set Listing State:</Form.Label>
            <Col sm={9}>
              <Form.Check type="switch" label={listingEnabled ? 'Enabled' : 'Disabled'} checked={listingEnabled} onChange={onListingChange} />
              {config.content?.listings?.length ? (
                <div className="mt-1 text-muted">
                  Applies to <strong>{config.content.listings.length}</strong> listing(s)
                </div>
              ) : (
                <div className="mt-1 text-warning">⚠️ No listings specified in this config</div>
              )}
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-2">
              <Form.Label column sm={3} className="text-right">Filename:</Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  placeholder="filename.json"
                  value={baseName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const val = e.target.value
                    setBaseName(val)
                    setFileName(`inventory-service/amazon/${val}`)
                  }}
                  isInvalid={existingConfigNames.includes(baseName)}
                />
                <Form.Text className={existingConfigNames.includes(baseName) ? 'text-danger' : 'text-muted'}>
                  {existingConfigNames.includes(baseName)
                    ? 'Filename already exists. Please choose another.'
                    : 'Must be unique and end with .json'}
                </Form.Text>
              </Col>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal} className="me-2">Cancel</Button>
        <Button variant="danger" onClick={handleDelete} className="me-2">Delete</Button>
        <Button variant="warning" onClick={duplicateConfig} id={config.name}>Duplicate</Button>
        <Button variant="success" onClick={handleSave}>Save</Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AmazonSFPConfigEdit